import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AccessibleSelect } from '../common';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';

const STATUS_OPTIONS = {
  epic: [
    { value: 'To Do', label: 'To Do' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Done', label: 'Done' },
  ],
  default: [
    { value: 'planned', label: 'Planned' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
  ],
};

const SprintModal = ({
  onClose,
  onSubmit,
  projectId,
  sprintData = null,
  isSubmitting = false,
  entityType = 'sprint',
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      summary: '',
      description: '',
      priority: 'medium',
      status: entityType === 'epic' ? 'To Do' : 'planned',
      assignee: '',
      start_date: '',
      end_date: '',
      story_point: 0,
    },
  });

  const startDate = watch('start_date');
  const status = watch('status');
  const busy = isSubmitting || isSubmitted;

  useEffect(() => {
    register('status', { required: 'Status is required' });
  }, [register]);

  useEffect(() => {
    if (sprintData) {
      const formValues = {
        name: sprintData.name || '',
        description: sprintData.description || '',
      };

      if (entityType === 'epic') {
        formValues.summary = sprintData.summary || sprintData.name || '';
        formValues.priority = sprintData.priority || 'medium';
        formValues.status = sprintData.status || 'To Do';
        formValues.assignee = sprintData.assignee || '';
        formValues.story_point = sprintData.story_point ?? 0;
        formValues.epic_id = sprintData.id;
      } else {
        formValues.status = sprintData.status || 'planned';
        formValues.start_date = sprintData.start_date?.split('T')[0] || '';
        formValues.end_date = sprintData.end_date?.split('T')[0] || '';
        if (entityType === 'release') {
          formValues.release_id = sprintData.release_id || sprintData.id;
        } else {
          formValues.sprint_id = sprintData.sprint_id || sprintData.id;
        }
      }

      reset(formValues);
      return;
    }

    if (entityType === 'epic') {
      setValue('priority', 'medium');
      setValue('status', 'To Do');
      setValue('story_point', 0);
    } else {
      setValue('status', 'planned');
      const today = new Date().toISOString().split('T')[0];
      const defaultEndDate = new Date();
      defaultEndDate.setDate(defaultEndDate.getDate() + (entityType === 'sprint' ? 14 : 30));
      setValue('start_date', today);
      setValue('end_date', defaultEndDate.toISOString().split('T')[0]);
    }
  }, [sprintData, reset, setValue, entityType]);

  const onSubmitForm = async (data) => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const submissionData = {
      ...data,
      project_id: projectId,
    };

    if (sprintData) {
      if (entityType === 'sprint') {
        submissionData.sprint_id = sprintData.sprint_id || sprintData.id;
      } else if (entityType === 'release') {
        submissionData.release_id = sprintData.release_id || sprintData.id;
      } else if (entityType === 'epic') {
        submissionData.epic_id = sprintData.id;
      }
    }

    try {
      await onSubmit(submissionData);
    } catch {
      setIsSubmitted(false);
    }
  };

  const getEntityLabels = () => {
    switch (entityType) {
      case 'release':
        return {
          title: sprintData ? 'Update Release' : 'Create Release',
          subtitle: sprintData
            ? 'Update release details and schedule.'
            : 'Define a release window for grouping test plans.',
          nameLabel: 'Release Name',
          buttonText: sprintData ? 'Update Release' : 'Create Release',
          loadingText: sprintData ? 'Updating…' : 'Creating…',
        };
      case 'epic':
        return {
          title: sprintData ? 'Update Epic' : 'Create Epic',
          subtitle: sprintData
            ? 'Update epic summary, priority, and ownership.'
            : 'Capture epic scope for linked test plans.',
          nameLabel: 'Epic Name',
          buttonText: sprintData ? 'Update Epic' : 'Create Epic',
          loadingText: sprintData ? 'Updating…' : 'Creating…',
        };
      default:
        return {
          title: sprintData ? 'Update Sprint' : 'Create Sprint',
          subtitle: sprintData
            ? 'Update sprint details and schedule.'
            : 'Plan a time-boxed sprint for execution.',
          nameLabel: 'Sprint Name',
          buttonText: sprintData ? 'Update Sprint' : 'Create Sprint',
          loadingText: sprintData ? 'Updating…' : 'Creating…',
        };
    }
  };

  const entityLabels = getEntityLabels();
  const statusOptions = entityType === 'epic' ? STATUS_OPTIONS.epic : STATUS_OPTIONS.default;

  return (
    <RtModal
      open
      onClose={onClose}
      title={entityLabels.title}
      subtitle={entityLabels.subtitle}
      size="md"
      closeDisabled={busy}
      closeOnBackdrop={!busy}
      footer={
        <>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="rt-sprint-entity-form"
            className="rt-btn rt-btn--primary"
            disabled={busy}
          >
            {busy ? entityLabels.loadingText : entityLabels.buttonText}
          </button>
        </>
      }
    >
      <form id="rt-sprint-entity-form" onSubmit={handleSubmit(onSubmitForm)} noValidate>
        <RtModalSection title={entityType === 'epic' ? 'Epic details' : `${entityType.charAt(0).toUpperCase()}${entityType.slice(1)} details`}>
          {entityType === 'epic' ? (
            <>
              <RtModalField
                label="Epic Summary"
                htmlFor="summary"
                required
                error={errors.summary?.message}
              >
                <input
                  id="summary"
                  type="text"
                  className={errors.summary ? 'error' : ''}
                  placeholder="Enter epic summary"
                  disabled={busy}
                  {...register('summary', {
                    required: 'Epic summary is required',
                    minLength: { value: 3, message: 'Summary must be at least 3 characters' },
                    maxLength: { value: 100, message: 'Summary cannot exceed 100 characters' },
                  })}
                />
              </RtModalField>
              <RtModalField label="Epic Name" htmlFor="name">
                <input
                  id="name"
                  type="text"
                  placeholder="Enter epic name (optional)"
                  disabled={busy}
                  {...register('name')}
                />
              </RtModalField>
            </>
          ) : (
            <RtModalField
              label={entityLabels.nameLabel}
              htmlFor="name"
              required
              error={errors.name?.message}
            >
              <input
                id="name"
                type="text"
                className={errors.name ? 'error' : ''}
                placeholder={`Enter ${entityType} name`}
                disabled={busy}
                {...register('name', {
                  required: `${entityType} name is required`,
                  minLength: { value: 3, message: 'Name must be at least 3 characters' },
                  maxLength: { value: 100, message: 'Name cannot exceed 100 characters' },
                  pattern: {
                    value: /^[a-zA-Z0-9\s\-_]+$/,
                    message: 'Only letters, numbers, spaces, hyphens and underscores allowed',
                  },
                })}
              />
            </RtModalField>
          )}

          <RtModalField label="Description" htmlFor="description" error={errors.description?.message}>
            <textarea
              id="description"
              placeholder={`Enter ${entityType} description`}
              rows={4}
              disabled={busy}
              {...register('description', {
                maxLength: { value: 500, message: 'Description cannot exceed 500 characters' },
              })}
            />
          </RtModalField>
        </RtModalSection>

        <RtModalSection title="Configuration">
          {entityType === 'epic' && (
            <div className="rt-modal__grid-2">
              <RtModalField label="Priority" htmlFor="priority" required error={errors.priority?.message}>
                <select
                  id="priority"
                  className={errors.priority ? 'error' : ''}
                  disabled={busy}
                  {...register('priority', { required: 'Priority is required' })}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </RtModalField>
              <RtModalField label="Story Points" htmlFor="story_point" error={errors.story_point?.message}>
                <input
                  id="story_point"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  disabled={busy}
                  {...register('story_point', {
                    min: { value: 0, message: 'Story points cannot be negative' },
                    valueAsNumber: true,
                  })}
                />
              </RtModalField>
            </div>
          )}

          <RtModalField label="Status" htmlFor="status" required error={errors.status?.message}>
            <AccessibleSelect
              id="status"
              value={status}
              onChange={(value) =>
                setValue('status', value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              options={statusOptions}
              placeholder="Select status"
              disabled={busy}
              required
              error={Boolean(errors.status)}
              aria-labelledby="status-label"
            />
          </RtModalField>

          {entityType === 'epic' && (
            <RtModalField label="Assignee" htmlFor="assignee" error={errors.assignee?.message}>
              <input
                id="assignee"
                type="text"
                placeholder="Type assignee name"
                disabled={busy}
                {...register('assignee', {
                  maxLength: {
                    value: 50,
                    message: 'Assignee name cannot exceed 50 characters',
                  },
                })}
              />
            </RtModalField>
          )}
        </RtModalSection>

        {(entityType === 'sprint' || entityType === 'release') && (
          <RtModalSection title="Schedule">
            <div className="rt-modal__grid-2">
              <RtModalField label="Start Date" htmlFor="start_date" required error={errors.start_date?.message}>
                <input
                  id="start_date"
                  type="date"
                  className={errors.start_date ? 'error' : ''}
                  disabled={busy}
                  {...register('start_date', { required: 'Start date is required' })}
                />
              </RtModalField>
              <RtModalField label="End Date" htmlFor="end_date" required error={errors.end_date?.message}>
                <input
                  id="end_date"
                  type="date"
                  className={errors.end_date ? 'error' : ''}
                  disabled={busy}
                  {...register('end_date', {
                    required: 'End date is required',
                    validate: {
                      afterStart: (value) =>
                        !startDate || value >= startDate || 'End date must be on or after start date',
                    },
                  })}
                />
              </RtModalField>
            </div>
          </RtModalSection>
        )}
      </form>
    </RtModal>
  );
};

export default SprintModal;
